import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import NotificationIcon from "./NotificationIcon";

function timeAgo(iso) {
    try {
        const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 30) return "just now";
        if (s < 90) return "1m ago";
        if (s < 3600) return `${Math.round(s / 60)}m ago`;
        if (s < 86400) return `${Math.round(s / 3600)}h ago`;
        return `${Math.round(s / 86400)}d ago`;
    } catch { return ""; }
}

export default function NotificationRow({ n, onRead, onRemove }) {
    const navigate = useNavigate();
    const unread = !n.read;

    const handleAction = () => {
        onRead?.(n.id);
        if (n.action_url) {
            if (n.action_url.startsWith("/")) navigate(n.action_url);
            else window.location.href = n.action_url;
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: -8, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: 12 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`notification-row-${n.id}`}
            data-read={n.read ? "true" : "false"}
            className={`
                group relative px-4 py-4
                border-b border-tca-cyan/10
                ${unread ? "bg-tca-cyan/[0.035]" : ""}
                hover:bg-tca-cyan/[0.06] transition-colors duration-200
            `}
        >
            <div className="flex items-start gap-3">
                <div className="relative mt-0.5">
                    <span className={`inline-block w-2 h-2 rounded-full ${unread ? "bg-tca-cyan shadow-[0_0_8px_rgba(0,219,255,0.7)]" : "bg-tca-ink-muted/45"}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <NotificationIcon type={n.type} size={14} className="text-tca-cyan flex-shrink-0" />
                            <span className="font-mono-tight text-[10px] uppercase tracking-[0.24em] text-tca-cyan/80 truncate">
                                {n.type.replace(/_/g, " ")}
                            </span>
                        </div>
                        <span className="font-mono-tight text-[9px] uppercase tracking-[0.22em] text-tca-ink-muted whitespace-nowrap">
                            {timeAgo(n.created_at)}
                        </span>
                    </div>
                    <div className="mt-1 text-[13px] text-tca-ink leading-snug">{n.title}</div>
                    {n.body && <div className="mt-1 text-[12px] text-tca-ink-muted leading-snug">{n.body}</div>}
                    {n.action_url && n.action_label && (
                        <button
                            type="button"
                            onClick={handleAction}
                            data-testid={`notification-action-${n.id}`}
                            className="mt-2 inline-flex items-center gap-1 font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-cyan-hi hover:text-white px-3 py-1.5 border border-tca-cyan/40 hover:border-tca-cyan rounded-full transition-all"
                        >
                            {n.action_label} →
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => onRemove?.(n.id)}
                    aria-label="Dismiss"
                    data-testid={`notification-dismiss-${n.id}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-tca-ink-muted hover:text-tca-magenta-accent"
                >
                    <Trash2 size={12} />
                </button>
            </div>
        </motion.div>
    );
}
